// "Import from cloud" row on the upload page — Google Photos, Google Drive,
// Dropbox. Buttons light up when the host has configured the (free) developer
// keys in the environment; otherwise a tap explains what's missing.
import React, { useState } from "react";
import { toast } from "sonner";
import { HardDrive, Cloud } from "lucide-react";
import { cloudConfig, pickDriveFiles, pickGooglePhotos, pickDropboxFiles } from "../lib/cloudImport";

const PROVIDERS = [
  { key: "googlePhotos", label: "Google Photos", picker: pickGooglePhotos, Icon: Cloud, color: "#34A853" },
  { key: "googleDrive", label: "Google Drive", picker: pickDriveFiles, Icon: HardDrive, color: "#4285F4" },
  { key: "dropbox", label: "Dropbox", picker: pickDropboxFiles, Icon: DropboxGlyph, color: "#0061FF" },
];

function DropboxGlyph({ size = 14, color = "currentColor" }) {
  // Simple Dropbox mark (lucide has no brand icons)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M6 2 12 6 6 10 0 6l6-4Zm12 0 6 4-6 4-6-4 6-4ZM0 14l6-4 6 4-6 4-6-4Zm18-4 6 4-6 4-6-4 6-4ZM6 19.5l6-4 6 4-6 4-6-4Z" />
    </svg>
  );
}

export default function CloudImportButtons({ onFiles }) {
  const [busyKey, setBusyKey] = useState(null);

  const run = async (provider) => {
    if (!cloudConfig[provider.key]) {
      toast.info(`${provider.label} import isn't set up yet — the site owner needs to add a free API key.`);
      return;
    }
    setBusyKey(provider.key);
    let tid = null;
    try {
      const files = await provider.picker({
        onDownloadStart: (n) => {
          tid = toast.loading(`Downloading ${n} file${n === 1 ? "" : "s"} from ${provider.label}…`);
        },
      });
      if (tid) toast.dismiss(tid);
      if (files.length > 0) {
        onFiles(files);
        toast.success(`Added ${files.length} file${files.length === 1 ? "" : "s"} from ${provider.label}`);
      }
    } catch (e) {
      if (tid) toast.dismiss(tid);
      toast.error(`${provider.label}: ${e.message || "import failed"}`);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="mt-3">
      <div className="text-[11px] text-center mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>
        or import from
      </div>
      <div className="flex flex-wrap justify-center gap-2" data-testid="cloud-import-row">
        {PROVIDERS.map((p) => {
          const configured = cloudConfig[p.key];
          const busy = busyKey === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => run(p)}
              disabled={busyKey !== null}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition"
              style={{
                background: configured ? `${p.color}18` : "rgba(255,255,255,0.03)",
                border: `1px solid ${configured ? `${p.color}66` : "rgba(255,255,255,0.10)"}`,
                color: configured ? "#fff" : "rgba(255,255,255,0.45)",
                opacity: busyKey !== null && !busy ? 0.5 : 1,
              }}
              data-testid={`cloud-import-${p.key}`}
              title={configured ? `Pick from ${p.label}` : `${p.label} — needs setup by the site owner`}
            >
              {busy ? <div className="meye-spinner" style={{ width: 12, height: 12 }} /> : <p.Icon size={14} color={configured ? p.color : "rgba(255,255,255,0.45)"} />}
              <span>{p.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
